import React from 'react';
import { ReactComponent as InfoIcon } from '../../../_shared/assets/icons/info-icon.svg';
import { Input } from '../../../_shared/components/Input/Input';
import { KpiTableProps, KpiRow, KpiYearData } from './types';
import styles from './FactsInputTableMobile.module.scss';

type FactField = 'fact6months' | 'fact9months' | 'fact12months';

function formatValue(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('ru-RU');
}

function toInputValue(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';
  return value.toString();
}

function parseInputValue(raw: string): number | null {
  return raw.trim() === '' ? null : Number(raw);
}

export function FactsInputTableMobile({
  rows,
  editable = false,
  onKpiClick,
  onEdit,
  onDelete,
  onMethodologyClick,
  onFactChange
}: KpiTableProps) {
  return (
    <div className={styles.list}>
      {rows.map((row, index) => (
        <KpiCard
          key={row.id}
          row={row}
          index={index}
          editable={editable}
          onKpiClick={onKpiClick}
          onEdit={onEdit}
          onDelete={onDelete}
          onMethodologyClick={onMethodologyClick}
          onFactChange={onFactChange}
        />
      ))}
    </div>
  );
}

interface CardProps extends Omit<KpiTableProps, 'rows'> {
  row: KpiRow;
  index: number;
}

function KpiCard({
  row,
  index,
  editable = false,
  onKpiClick,
  onMethodologyClick,
  onFactChange
}: CardProps) {
  const years = row.years.map((y) => y.year);

  const renderFactInput = (y: KpiYearData, field: FactField, thin = false) =>
    editable ? (
      <Input
        label=""
        type="number"
        value={toInputValue(y[field])}
        variant={thin ? 'thin' : undefined}
        onChange={(e) =>
          onFactChange?.(row.id, y.year, field, parseInputValue(e.target.value))
        }
      />
    ) : (
      formatValue(y[field])
    );

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <button className={styles.nameLink} onClick={() => onKpiClick?.(row)}>
          <span className={styles.nameIndex}>{index + 1}</span>
          {row.name}
        </button>
      </div>

      <div className={styles.meta}>
        <span className={styles.metaItem}>
          <span className={styles.metaLabel}>Ед. изм.</span>
          {row.unit}
        </span>
        <span className={styles.metaSpacer} />
        <span className={styles.metaItem}>
          <span className={styles.metaLabel}>Вес</span>
          {row.weight}
        </span>
        <button
          className={styles.methodLink}
          onClick={() => onMethodologyClick?.(row)}
        >
          Методика&nbsp; <InfoIcon className={styles.infoIcon} />
        </button>
      </div>

      <div className={styles.valuesGrid}>
        <div className={styles.tabletHeader}>
          <div className={styles.yearCell} />
          <div className={styles.planHeader}>План</div>
          <div className={styles.planFacts}>Факт, 6 мес</div>
          <div className={styles.planFacts}>Факт, 9 мес</div>
          <div className={styles.factHeader}>Факт, год</div>
        </div>

        {row.years.map((y) => (
          <div key={y.year} className={styles.tabletRow}>
            <div className={styles.yearCell}>{y.year}</div>
            <div className={styles.planCell}>{formatValue(y.plan)}</div>

            <div className={styles.factCellMonth}>
              {renderFactInput(y, 'fact6months', true)}
            </div>

            <div className={styles.factCellMonth}>
              {renderFactInput(y, 'fact9months', true)}
            </div>

            <div className={styles.factCell}>
              {renderFactInput(y, 'fact12months', true)}
            </div>
          </div>
        ))}

        <div className={styles.mobileHeader}>
          <div className={styles.mobileHeaderLabel} />
          {years.map((year) => (
            <div key={year} className={styles.mobileHeaderYear}>
              {year}
            </div>
          ))}
        </div>

        <div className={styles.mobilePlanRow}>
          <div className={styles.mobilePlanLabel}>План</div>
          {row.years.map((y) => (
            <div key={y.year} className={styles.mobilePlanCell}>
              {formatValue(y.plan)}
            </div>
          ))}
        </div>

        <div className={styles.mobileFactMonthRow}>
          <div className={styles.mobileFactMonthLabel}>Факт, 6 мес</div>
          {row.years.map((y) => (
            <div key={y.year} className={styles.mobileFactCell}>
              {renderFactInput(y, 'fact6months')}
            </div>
          ))}
        </div>

        <div className={styles.mobileFactMonthRow}>
          <div className={styles.mobileFactMonthLabel}>Факт, 9 мес</div>
          {row.years.map((y) => (
            <div key={y.year} className={styles.mobileFactCell}>
              {renderFactInput(y, 'fact9months')}
            </div>
          ))}
        </div>

        <div className={styles.mobileFactRow}>
          <div className={styles.mobileFactLabel}>Факт, год</div>
          {row.years.map((y) => (
            <div key={y.year} className={styles.mobileFactCell}>
              {renderFactInput(y, 'fact12months')}
            </div>
          ))}
        </div>
      </div>

      <div className={styles.dept}>
        <span className={styles.deptLabel}>Оценивающее подразделение:</span>
        <span className={styles.deptValue}>{row.department}</span>
      </div>
    </div>
  );
}
